/**
 * BroadcastPreviewModal - Modal de preview e confirmação de envio
 * Versão mobile-first
 */
import React from 'react'
import ReactMarkdown from 'react-markdown'
import { Bell, Mail, Users, Smartphone, AlertTriangle, Send } from 'lucide-react'
import { Modal } from '../../components/molecules/Modal'
import Card from '../../components/atoms/Card'
import Button from '../../components/atoms/Button'
import Avatar from '../../components/atoms/Avatar'
import { formatNumber } from '../../core/admin.utils'
import { MARKDOWN_PREVIEW_STYLES } from '../core/broadcast.constants'

const BroadcastPreviewModal = ({
	isOpen,
	onClose,
	form,
	previewStats,
	loading,
	onConfirm,
}) => {
	const previewData = previewStats
	const sendLoading = loading
	const onSend = onConfirm
	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Confirmar Envio"
			size="lg"
		>
			<div className="space-y-4 md:space-y-6">
				{/* Estatísticas de Alcance */}
				<div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
					<Card className="p-3 md:p-4 text-center">
						<Users className="w-6 h-6 md:w-8 md:h-8 mx-auto mb-1 md:mb-2 text-primary" />
						<p className="text-xl md:text-2xl font-bold text-text">
							{formatNumber(previewData?.totalTargeted || 0)}
						</p>
						<p className="text-xs md:text-sm text-text-muted">Total de usuários</p>
					</Card>
					<Card className="p-3 md:p-4 text-center">
						<Smartphone className="w-6 h-6 md:w-8 md:h-8 mx-auto mb-1 md:mb-2 text-blue-500" />
						<p className="text-xl md:text-2xl font-bold text-text">
							{formatNumber(previewData?.byPlatform?.withPushToken || 0)}
						</p>
						<p className="text-xs md:text-sm text-text-muted">Com push ativo</p>
					</Card>
					<Card className="p-3 md:p-4 text-center">
						<Mail className="w-6 h-6 md:w-8 md:h-8 mx-auto mb-1 md:mb-2 text-green-500" />
						<p className="text-xl md:text-2xl font-bold text-text">
							{formatNumber(previewData?.byPlatform?.withEmail || 0)}
						</p>
						<p className="text-xs md:text-sm text-text-muted">Com email</p>
					</Card>
				</div>

				<style>{MARKDOWN_PREVIEW_STYLES}</style>

				{/* Preview Push */}
				{(form.type === 'push' || form.type === 'both') && (
					<Card className="p-3 md:p-4">
						<div className="flex items-center gap-2 mb-2 md:mb-3">
							<Bell className="w-4 h-4 md:w-5 md:h-5 text-blue-500" />
							<p className="text-xs md:text-sm font-semibold text-text">Preview - Push</p>
						</div>
						<div className="bg-base-400 rounded-lg p-3 md:p-4">
							<p className="font-semibold text-text text-sm md:text-base mb-2">{form.pushTitle}</p>
							<div className="markdown-preview text-text-body text-sm">
								<ReactMarkdown>{form.pushMessage}</ReactMarkdown>
							</div>
						</div>
					</Card>
				)}

				{/* Preview Email */}
				{(form.type === 'email' || form.type === 'both') && (
					<Card className="p-3 md:p-4">
						<div className="flex items-center gap-2 mb-2 md:mb-3">
							<Mail className="w-4 h-4 md:w-5 md:h-5 text-green-500" />
							<p className="text-xs md:text-sm font-semibold text-text">Preview - E-mail</p>
						</div>
						<div className="bg-base-400 rounded-lg p-3 md:p-4">
							<p className="font-semibold text-text text-sm md:text-base mb-2">{form.emailSubject}</p>
							{form.emailTitle && (
								<p className="font-medium text-text text-sm mb-2">{form.emailTitle}</p>
							)}
							<div className="markdown-preview text-text-body text-sm">
								<ReactMarkdown>{form.emailBody}</ReactMarkdown>
							</div>
						</div>
					</Card>
				)}

				{/* Amostra de usuários - escondido em mobile */}
				{previewData?.sampleUsers?.length > 0 && (
					<Card className="p-3 md:p-4 hidden sm:block">
						<p className="text-xs md:text-sm text-text-muted mb-2 md:mb-3">
							Amostra de destinatários ({Math.min(5, previewData.totalTargeted)} de {previewData.totalTargeted})
						</p>
						<div className="space-y-2">
							{previewData.sampleUsers.map((user) => (
								<div key={user.id} className="flex items-center gap-2 md:gap-3 p-2 bg-base-400 rounded-lg">
									<Avatar name={user.name} size="sm" />
									<div className="flex-1 min-w-0">
										<p className="text-xs md:text-sm font-medium text-text truncate">{user.name}</p>
										<p className="text-xs text-text-muted truncate">{user.email}</p>
									</div>
									<div className="flex gap-1">
										{user.hasPushToken && (
											<Smartphone className="w-3 h-3 md:w-4 md:h-4 text-blue-500" title="Push ativo" />
										)}
										{user.email && (
											<Mail className="w-3 h-3 md:w-4 md:h-4 text-green-500" title="Email disponível" />
										)}
									</div>
								</div>
							))}
						</div>
					</Card>
				)}

				{/* Aviso */}
				<div className="p-3 md:p-4 bg-warning/10 border border-warning/30 rounded-lg flex items-start gap-2 md:gap-3">
					<AlertTriangle className="w-4 h-4 md:w-5 md:h-5 text-warning flex-shrink-0 mt-0.5" />
					<div>
						<p className="text-xs md:text-sm font-medium text-warning">Atenção</p>
						<p className="text-xs md:text-sm text-text-body">
							Esta ação enviará a mensagem para {formatNumber(previewData?.totalTargeted || 0)} usuários.
						</p>
					</div>
				</div>

				{/* Actions - Mobile friendly */}
				<div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 pt-2 border-t border-base-200 sm:border-0 sm:pt-0">
					<Button variant="outline" onClick={onClose} className="w-full sm:w-auto">
						Cancelar
					</Button>
					<Button
						onClick={onSend}
						loading={sendLoading}
						disabled={!previewData?.totalTargeted}
						className="w-full sm:w-auto"
					>
						<Send className="w-4 h-4 mr-2" />
						<span className="hidden sm:inline">Confirmar Envio</span>
						<span className="sm:hidden">Enviar</span>
						<span className="ml-1">({formatNumber(previewData?.totalTargeted || 0)})</span>
					</Button>
				</div>
			</div>
		</Modal>
	)
}

export default BroadcastPreviewModal
